import { signal } from '@preact/signals';
import { useEffect, useRef, useState } from 'preact/hooks';
import {
  calendarFilmOpen, hadSettings, introOpen, loadJSON, onceOfferShown, panel, pointingPaused, pointingPrompt, reliving, saveJSON, selection,
  setSetting, settings, storyOpen, viewMode, whatToShow,
} from '../state';
import { guideOffer } from './Guide';
import { openIslam } from './IslamPanel';
import { Icon } from './icons';

// Offers made once, on the sky, at a quiet moment, in the look of "New to Ahillah?" (src/ui/Guide.tsx): the Arabic names
// to an install from before they were on from the first run, which has them off (BUILD-SPEC item 10), and "The stars in
// Islam" after the tour of the buttons (item 45). Answered, or shown and left until the page closes, an offer never
// comes back.

type OfferId = 'arabic' | 'islam';
type OfferState = Partial<Record<OfferId, 'pending' | 'done'>>;

/** `title`: a heading above the line, where it has one. */
const OFFERS: Record<OfferId, { icon: string; title?: string; text: string; yes: string; act: () => void }> = {
  arabic: {
    icon: 'star',
    title: 'Arabic names',
    text: 'Show the Arabic names of the stars, the planets, the Sun and Moon and more on their cards?',
    yes: 'Show them',
    act: () => setSetting('arabic', true),
  },
  // The owner's words (A15): one line.
  islam: { icon: 'book', text: 'Read “The stars in Islam”: what the Quran and the Sunnah say about the sky.', yes: 'Read it', act: () => openIslam() },
};
const ORDER: OfferId[] = ['arabic', 'islam'];
/** At least this long between two offers. */
const GAP = 60e3;

// The first start of this version decides the Arabic offer, once: only an install from before, with Arabic off. (One
// moved from the old address is decided the same way at its first start here: src/lib/move.ts clears the offers.)
const first = loadJSON<OfferState | null>('offers', null);
const offers = signal<OfferState>(first ?? { arabic: hadSettings && !settings.peek().arabic ? 'pending' : 'done' });
if (!first) saveJSON('offers', offers.peek());

function mark(id: OfferId, v: 'pending' | 'done') {
  offers.value = { ...offers.peek(), [id]: v };
  saveJSON('offers', offers.peek());
}

// "The stars in Islam" after the tour of the buttons, however it ends.
let touring = panel.peek() === 'walkthrough';
panel.subscribe((p) => {
  if (touring && p !== 'walkthrough' && !offers.peek().islam) mark('islam', 'pending');
  touring = p === 'walkthrough';
});

let lastClosed = 0;

export function OnceOffers({ blocked }: { blocked: boolean }) {
  const pending = ORDER.find((k) => offers.value[k] === 'pending');
  const [shown, setShown] = useState<OfferId | null>(null);
  // Only while Ahillah is on screen: an offer is never used up in a tab left in the background.
  const [onScreen, setOnScreen] = useState(() => typeof document === 'undefined' || document.visibilityState === 'visible');
  useEffect(() => {
    const seen = () => setOnScreen(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', seen);
    return () => document.removeEventListener('visibilitychange', seen);
  }, []);
  const ref = useRef<HTMLDivElement>(null);
  const before = useRef<HTMLElement | null>(null);
  const focused = useRef<OfferId | null>(null);
  // Quiet: nothing else asks for attention, the phone is not being followed, and "New to Ahillah?" is not due.
  const idle = onScreen && !blocked && panel.value === null && !selection.value && viewMode.value === 'manual' && !pointingPrompt.value && !pointingPaused.value
    && !reliving.value && !introOpen.value && !calendarFilmOpen.value && !storyOpen.value && !whatToShow.value
    && guideOffer.value !== 'pending' && guideOffer.value !== 'shown';
  useEffect(() => {
    if (!pending || shown || !idle) return;
    // A moment's look round first (longer for the Arabic offer, which comes as the app opens), and never straight after
    // another offer.
    const wait = Math.max(pending === 'arabic' ? 8000 : 1500, lastClosed + GAP - Date.now());
    const id = setTimeout(() => {
      mark(pending, 'done');
      setShown(pending);
    }, wait);
    return () => clearTimeout(id);
  }, [pending, shown, idle]);
  const visible = shown !== null && idle;
  useEffect(() => {
    onceOfferShown.value = visible;
  }, [visible]);
  /** `back`: focus goes back where it was before the offer took it (not when the answer opens something). */
  const close = (back = true) => {
    setShown(null);
    lastClosed = Date.now();
    const to = before.current;
    before.current = null;
    if (back) setTimeout(() => (to && document.contains(to) ? to : null)?.focus({ preventScroll: true }), 0);
  };
  // Focus comes to the offer the first time it shows, so a screen reader reads it; Escape is Not now.
  useEffect(() => {
    if (!visible) return;
    if (focused.current !== shown) {
      focused.current = shown;
      before.current = document.activeElement as HTMLElement | null;
      ref.current?.focus({ preventScroll: true });
    }
    const key = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      close();
    };
    addEventListener('keydown', key);
    return () => removeEventListener('keydown', key);
  }, [visible]);
  if (!visible || !shown) return null;
  const o = OFFERS[shown];
  return (
    <div class="guide-offer" role="dialog" aria-labelledby={o.title ? 'once-offer-title' : 'once-offer-text'} aria-describedby={o.title ? 'once-offer-text' : undefined} tabIndex={-1} ref={ref}>
      {o.title && <h2 id="once-offer-title"><span class="guide-offer-icon" aria-hidden="true"><Icon name={o.icon} size={18} stroke={1.9} /></span>{o.title}</h2>}
      <p id="once-offer-text">{o.text}</p>
      <div class="guide-offer-actions">
        <button class="btn-ghost" onClick={() => close()}>Not now</button>
        <button class="btn" onClick={() => { close(false); o.act(); }}>{o.yes}</button>
      </div>
    </div>
  );
}
